import { useState, useEffect, useCallback } from 'react';

interface UsePdfViewerProps {
  url: string;
}

interface UsePdfViewerResult {
  isLoading: boolean;
  isError: boolean;
  errorType?: 'network' | 'invalid_url' | 'access_denied' | 'unknown';
  handleError: () => void;
  retryLoading: () => void;
}

/**
 * Custom hook to handle PDF viewer state and loading
 */
const usePdfViewer = ({ url }: UsePdfViewerProps): UsePdfViewerResult => {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorType, setErrorType] = useState<
    'network' | 'invalid_url' | 'access_denied' | 'unknown'
  >();

  // Function to validate and check URL
  const validateUrl = useCallback(
    (abortController: AbortController) => {
      if (!url) {
        setIsError(true);
        setErrorType('invalid_url');
        setIsLoading(false);
        return;
      }

      // Simple URL validation
      try {
        new URL(url);

        // Check if the URL is accessible by making a HEAD request
        // This is optional and can be removed if causing CORS issues
        fetch(url, {
          method: 'HEAD',
          signal: abortController.signal,
        })
          .then((response) => {
            if (!response.ok) {
              if (response.status === 403 || response.status === 401) {
                setErrorType('access_denied');
              } else {
                setErrorType('network');
              }
              // Don't set error state yet, let the PDF viewer try to load it
            }
            setIsLoading(false);
          })
          .catch((error) => {
            // Ignore aborted requests
            if (error.name === 'AbortError') return;

            // Just log the error but don't set error state yet
            // We'll let the PDF viewer component try to load it first
            // eslint-disable-next-line no-console
            console.warn('PDF URL may not be accessible:', error);
            setErrorType('network');
            setIsLoading(false);
          });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Invalid PDF URL:', url);
        setIsError(true);
        setErrorType('invalid_url');
        setIsLoading(false);
      }
    },
    [url],
  );

  // Retry loading function
  const retryLoading = useCallback(() => {
    setIsLoading(true);
    setIsError(false);
    setErrorType(undefined);

    const abortController = new AbortController();
    validateUrl(abortController);

    return () => abortController.abort();
  }, [validateUrl]);

  useEffect(() => {
    // Reset states when URL changes
    setIsLoading(true);
    setIsError(false);
    setErrorType(undefined);

    // Create abort controller for cleanup
    const abortController = new AbortController();
    validateUrl(abortController);

    // Cleanup function to handle component unmounting
    return () => {
      // Cancel any pending fetch operations
      abortController.abort();
    };
  }, [url, validateUrl]);

  const handleError = useCallback(() => {
    // eslint-disable-next-line no-console
    console.error('Failed to load PDF:', url);
    setIsError(true);
    setErrorType(errorType || 'unknown');
    setIsLoading(false);
  }, [url, errorType]);

  return {
    isLoading,
    isError,
    errorType,
    handleError,
    retryLoading,
  };
};

export default usePdfViewer;
